import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/vb";
import { CountrySelect } from "./CountrySelect";
import { countryName } from "./countries";
import {
  PLACES_API_KEY,
  autocompletePlaces,
  resolvePlace,
  newSessionToken,
  type Prediction,
} from "./googlePlaces";
import { MAX_MARKET_CITY } from "./useBuyerWizard";

type Props = {
  region: string; // market_region (ISO-2)
  city: string; // market_city (formatted area, "" for country-only)
  onChange: (next: { region: string; city: string }) => void;
};

/** Today's manual inputs: country dropdown + free-text area. The fallback when
 *  no Places key is configured (or a request fails), so the form always works. */
function ManualLocation({ region, city, onChange }: Props) {
  return (
    <>
      <div className="pf-input">
        <span className="pf-label">Country</span>
        <CountrySelect value={region} onChange={(code) => onChange({ region: code, city })} />
      </div>
      <div className="pf-input">
        <span className="pf-label">
          Specific area{" "}
          <span style={{ fontWeight: 500, color: "var(--vb-text-3)" }}>· optional</span>
        </span>
        {/* Free text, unlike the Places path whose values are canonical — so
            it needs the same cap the API applies to market_city. */}
        <Input
          size="lg"
          type="text"
          value={city}
          maxLength={MAX_MARKET_CITY}
          placeholder="e.g. London, England"
          onChange={(e) => onChange({ region, city: e.target.value })}
          aria-label="Specific area"
        />
      </div>
    </>
  );
}

/** Single location combobox backed by the Places API (New) REST autocomplete.
 *  Renders our own dropdown (in a portal so the picker's overflow doesn't clip
 *  it). A country pick sets region only; a city pick sets region + city. */
function PlacesLocation({ region, city, onChange }: Props) {
  const display = city || countryName(region) || "";
  const [query, setQuery] = useState(display);
  const [preds, setPreds] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [failed, setFailed] = useState(false);
  const [, force] = useState(0); // reposition tick on scroll/resize

  const inputRef = useRef<HTMLInputElement>(null);
  const session = useRef(newSessionToken());
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  const abort = useRef<AbortController>();
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  // Reflect external changes (Marcus suggestion / a pick) into the input, but
  // never overwrite what the user is actively typing: if the field is focused, a
  // suggestion resolving mid-keystroke must not clobber their in-progress query.
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setQuery(city || countryName(region) || "");
  }, [region, city]);

  // Keep the portal dropdown glued under the input while it's open.
  useEffect(() => {
    if (!open) return;
    const onMove = () => force((n) => n + 1);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  // On unmount (e.g. switching field cards), drop any pending timers/request so
  // they can't fire setState on an unmounted component or bill a stray call.
  useEffect(
    () => () => {
      clearTimeout(debounce.current);
      clearTimeout(blurTimer.current);
      abort.current?.abort();
    },
    [],
  );

  const search = useCallback((value: string) => {
    clearTimeout(debounce.current);
    if (!value.trim()) {
      setPreds([]);
      setOpen(false);
      return;
    }
    debounce.current = setTimeout(async () => {
      abort.current?.abort();
      abort.current = new AbortController();
      try {
        const p = await autocompletePlaces(value, session.current, abort.current.signal);
        setPreds(p);
        setActive(-1);
        setOpen(p.length > 0);
      } catch (err) {
        if ((err as Error).name !== "AbortError") setFailed(true);
      }
    }, 250);
  }, []);

  const pick = useCallback(
    async (pred: Prediction) => {
      setOpen(false);
      const prior = city || countryName(region) || "";
      setQuery(pred.main + (pred.secondary ? `, ${pred.secondary}` : ""));
      try {
        const resolved = await resolvePlace(pred.placeId, session.current);
        if (resolved) {
          session.current = newSessionToken(); // close the billed session on success only
          onChange(resolved);
          setQuery(resolved.city || countryName(resolved.region) || pred.main);
        } else {
          // No derivable country (rare) - don't leave the input lying; restore.
          setQuery(prior);
        }
      } catch {
        // Transient details failure: autocomplete still works, so stay in the
        // Places field and restore the prior value rather than dropping to manual.
        setQuery(prior);
      }
    },
    [onChange, city, region],
  );

  if (failed) return <ManualLocation region={region} city={city} onChange={onChange} />;

  const rect = inputRef.current?.getBoundingClientRect();

  return (
    <div className="pf-input">
      <span className="pf-label">Location</span>
      <Input
        size="lg"
        ref={inputRef}
        type="text"
        value={query}
        placeholder="Country, city, or area"
        aria-label="Market region location"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls="vb-ac-list"
        aria-activedescendant={open && active >= 0 ? `vb-ac-opt-${active}` : undefined}
        onChange={(e) => {
          setQuery(e.target.value);
          search(e.target.value);
        }}
        onFocus={() => preds.length && setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (preds.length) setOpen(true);
            setActive((a) => Math.min(a + 1, preds.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => (a <= 0 ? a : a - 1));
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (open && active >= 0 && preds[active]) void pick(preds[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      <span className="help">
        Start typing a country or city and pick a suggestion. A country on its own is fine.
      </span>

      {open && rect && preds.length > 0 &&
        createPortal(
          <div
            className="vb-ac__pop vb-root"
            style={{ position: "fixed", top: rect.bottom + 4, left: rect.left, width: rect.width }}
            onMouseDown={(e) => e.preventDefault() /* keep input focused through the click */}
          >
            <ul id="vb-ac-list" role="listbox" className="vb-ac__list">
              {preds.map((p, i) => (
                <li
                  key={p.placeId}
                  id={`vb-ac-opt-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className={`vb-ac__item${i === active ? " is-active" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => {
                    clearTimeout(blurTimer.current);
                    void pick(p);
                  }}
                >
                  <span className="vb-ac__main">{p.main}</span>
                  {p.secondary && <span className="vb-ac__sec">{p.secondary}</span>}
                </li>
              ))}
            </ul>
            {/* Required attribution when showing Places predictions off a map. */}
            <div className="vb-ac__attr">
              <img
                src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png"
                alt="Powered by Google"
                height={14}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Market-region input: a single Places (New) combobox when a key is configured,
 *  otherwise the manual country + area inputs. */
export function LocationField(props: Props) {
  return PLACES_API_KEY ? <PlacesLocation {...props} /> : <ManualLocation {...props} />;
}

export default LocationField;
